import { useMemo, useState, useEffect, useCallback } from 'react';

import { trackEvent } from 'src/utils/google-tag-manager';

import { useBilling } from 'src/billing';
import { endpoints } from 'src/lib/axios';
import { getConfig } from 'src/global-config';
import { deriveBillingState } from 'src/billing/context/helpers';

import { getPlanChangeType } from '../utils/planHierarchy';
import {
  pendingPlanKey,
  readPendingPlan,
  CANCEL_EXPIRY_MS,
  readPendingCoupon,
  clearPendingCoupon,
  optimisticCancelKey,
} from '../types/billing';

import type { Invoice, TrialKind, PlanLimits, PlanWarning, BillingState, UsageSummary, PaymentMethod, CreditBalance, CreditPricing, ActivePlanGrant, RawSubscription, SubscriptionWarning } from '../types/billing';

// SWR key for the org-status fetch in BillingProvider. Exported so external
// hooks (e.g. useCreditPurchaseReturn) can mutate the cache without hardcoding
// the URL string.
export const ORG_STATUS_SWR_KEY = endpoints.billing.orgStatus;

export type UseSubscriptionReturn = {
  subscription: RawSubscription | null;
  invoices: Invoice[] | null;
  paymentMethod: PaymentMethod | null;
  billingState: BillingState;
  subscriptionWarning: SubscriptionWarning;
  currentPlan: string | null;
  planLimits: PlanLimits | null;
  currentUsage: UsageSummary | null;
  warnings: PlanWarning[] | null;
  creditBalances: CreditBalance[];
  creditPricing: CreditPricing | null;
  /** Free-months grant currently running. Null for a normal paying subscription. */
  activePlanGrant: ActivePlanGrant | null;
  /** Whether credit top-ups are allowed right now. Decided server-side. */
  canBuyPack: boolean;
  /**
   * Why the subscription is in a trial, or null when it is not. Drives banner wording:
   * free months earned with a code read differently from the events trial.
   */
  trialKind: TrialKind | null;
  isEventsTrialEligible: boolean;
  eventsTrialSource: string | null;
  eventsTrialStart: string | null;
  eventsTrialEnd: string | null;
  isDevAccount: boolean;
  orgId: string | null;
  isLoading: boolean;
  error: unknown;
  mutate: () => void;
  setOptimisticCancel: (value: boolean) => void;
};

export function useSubscription(): UseSubscriptionReturn {
  const {
    orgId,
    orgStatus,
    subscription,
    invoices,
    paymentMethod,
    serverCurrentPlan,
    isDevAccount,
    planLimits,
    currentUsage,
    warnings,
    creditBalances,
    creditPricing,
    activePlanGrant,
    canBuyPack,
    subscriptionWarning,
    isBillingLoading,
    billingError,
    revalidateBilling,
  } = useBilling();
  const { appMode } = getConfig();

  // Read optimistic cancel flag from localStorage on init —
  // persists across page refresh for local dev where webhooks don't fire.
  // Exposed via setOptimisticCancel so useBillingActions can update it immediately
  // without waiting for a SWR revalidation cycle.
  const [optimisticCancelAtPeriodEnd, setOptimisticCancelAtPeriodEnd] = useState<boolean>(() => {
    if (typeof window !== 'undefined' && orgId) {
      const ts = localStorage.getItem(optimisticCancelKey(orgId));
      if (ts && Date.now() - Number(ts) < CANCEL_EXPIRY_MS) return true;
    }
    return false;
  });

  const setOptimisticCancel = useCallback(
    (value: boolean) => {
      setOptimisticCancelAtPeriodEnd(value);
      if (!orgId) return;
      if (value) {
        localStorage.setItem(optimisticCancelKey(orgId), String(Date.now()));
      } else {
        localStorage.removeItem(optimisticCancelKey(orgId));
      }
    },
    [orgId]
  );

  const currentPlan = useMemo<string | null>(() => {
    if (billingError) return null;
    // Fallback to optimistic pending plan if DB still shows free (webhook not yet fired).
    // readPendingPlan() returns null if the flag is expired (checkout abandoned).
    const pendingPlan = orgId ? readPendingPlan(orgId) : null;
    return serverCurrentPlan && serverCurrentPlan !== 'free'
      ? serverCurrentPlan
      : pendingPlan ?? serverCurrentPlan;
  }, [serverCurrentPlan, billingError, orgId]);

  // Clear pending plan immediately if Stripe redirects back with ?checkout=canceled
  // (user clicked "Back" or closed the Stripe checkout tab without paying)
  useEffect(() => {
    if (!orgId) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'canceled') {
      const fromPlan = currentPlan;
      const toPlan = readPendingPlan(orgId);
      trackEvent('failed_subscription', {
        environment: appMode,
        funnel_name: 'upgrade_funnel',
        failure_stage: 'checkout_canceled',
        from_plan: fromPlan,
        to_plan: toPlan,
        change_type: getPlanChangeType(fromPlan, toPlan),
      });
      localStorage.removeItem(pendingPlanKey(orgId));
      clearPendingCoupon(orgId);
      params.delete('checkout');
      const qs = params.toString();
      window.history.replaceState({}, '', qs ? `?${qs}` : window.location.pathname);
    }
  }, [orgId]);

  // Clear pending plan when API definitively reports no subscription + free plan.
  // Prevents stale localStorage from overriding a server-confirmed free state.
  useEffect(() => {
    if (!orgId) return;
    if (!subscription && (!serverCurrentPlan || serverCurrentPlan === 'free')) {
      localStorage.removeItem(pendingPlanKey(orgId));
      clearPendingCoupon(orgId);
    }
  }, [orgId, subscription, serverCurrentPlan]);

  // Auto-clear the optimistic cancel flag when the API confirms the subscription is
  // active and not scheduled to cancel. Guards against stale localStorage being read
  // on remount (e.g. after tab switch) following a cancel → re-subscribe sequence.
  useEffect(() => {
    if (!optimisticCancelAtPeriodEnd || !subscription) return;
    const apiCancelAtPeriodEnd = subscription.cancelAtPeriodEnd;
    const subStatus = String(subscription.status || '').toLowerCase();
    if (apiCancelAtPeriodEnd === false && subStatus !== 'canceled' && subStatus !== 'cancelled') {
      setOptimisticCancel(false);
    }
  }, [subscription, optimisticCancelAtPeriodEnd, setOptimisticCancel]);

  const billingState = useMemo<BillingState>(
    () => deriveBillingState(currentPlan, subscription, optimisticCancelAtPeriodEnd),
    [currentPlan, subscription, optimisticCancelAtPeriodEnd]
  );

  const trialKind = useMemo<TrialKind | null>(() => {
    if (!subscription?.isInTrial) return null;
    if (subscription.trialSource) return subscription.trialSource;

    // Right after checkout the webhook may not have landed, so the subscription carries no
    // provenance yet. The code the user just entered tells us what this trial is, which keeps
    // the banner correct from the first paint instead of flashing the wrong wording.
    if (orgId && readPendingCoupon(orgId)) return 'coupon';

    // Older rows have no TrialSource. The events trial was the only kind before coupons, so
    // that is the safe reading.
    return 'events';
  }, [subscription, orgId]);

  const isEventsTrialEligible = useMemo<boolean>(() => {
    if (!orgStatus) return false;
    const raw = orgStatus as Record<string, unknown>;
    const org = (raw.organization || raw.Organization) as Record<string, unknown> | undefined;
    return Boolean(org?.isEventsTrialEligible ?? org?.IsEventsTrialEligible ?? false);
  }, [orgStatus]);

  const eventsTrialSource = useMemo<string | null>(() => {
    if (!orgStatus) return null;
    const raw = orgStatus as Record<string, unknown>;
    const org = (raw.organization || raw.Organization) as Record<string, unknown> | undefined;
    return ((org?.eventsTrialSource || org?.EventsTrialSource) as string | undefined) ?? null;
  }, [orgStatus]);

  const eventsTrialStart = useMemo<string | null>(() => {
    if (!orgStatus) return null;
    const raw = orgStatus as Record<string, unknown>;
    const org = (raw.organization || raw.Organization) as Record<string, unknown> | undefined;
    return ((org?.eventsTrialStart || org?.EventsTrialStart || org?.eventsTrialStartDate || org?.EventsTrialStartDate) as string | undefined) ?? null;
  }, [orgStatus]);

  const eventsTrialEnd = useMemo<string | null>(() => {
    if (!orgStatus) return null;
    const raw = orgStatus as Record<string, unknown>;
    const org = (raw.organization || raw.Organization) as Record<string, unknown> | undefined;
    return ((org?.eventsTrialEnd || org?.EventsTrialEnd || org?.eventsTrialEndDate || org?.EventsTrialEndDate) as string | undefined) ?? null;
  }, [orgStatus]);

  return {
    subscription,
    invoices,
    paymentMethod,
    billingState,
    subscriptionWarning,
    currentPlan,
    planLimits,
    currentUsage,
    warnings,
    creditBalances,
    creditPricing,
    activePlanGrant,
    canBuyPack,
    trialKind,
    isEventsTrialEligible,
    eventsTrialSource,
    eventsTrialStart,
    eventsTrialEnd,
    isDevAccount,
    orgId,
    isLoading: isBillingLoading,
    error: billingError ?? null,
    mutate: () => {
      void revalidateBilling();
    },
    setOptimisticCancel,
  };
}
